import { cosemApi } from '@services/providers/cosem';
import {
    MemberToCreate,
    MemberTable,
    SocialBenefit,
    MemberSocialBenefitToCreate,
    MemberSocialBenefitToUpdate,
    FinancialBenefit,
    FinancialBenefitToCreate,
    FinancialBenefitToUpdate,
    Contribution,
    ContributionToUpdate,
    ContributionToCreate,
    ContributionToCheck,
    FinancialBenefitScheduleToUpdate
} from './member.types';
import { ApiPaginationResponse, ApiResponse } from '@services/providers/cosem.types';
import { Member } from './member.types';
import { CdnFile } from '@services/core/cdn/file/file';
import { ActiveFilter } from '@utils/filters';
import { Ticketing, TicketingTable } from '../ticketing/ticketing.types';
import { BookingRange, BookingTable } from '../booking/booking.types';

const memberApi = cosemApi.injectEndpoints({
    endpoints: (build) => ({
        getMembers: build.mutation<
            ApiPaginationResponse<MemberTable>,
            { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/members`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<MemberTable>) => response
        }),
        getMembersEdition: build.mutation<
            ApiPaginationResponse<MemberTable>,
            { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/members/edition`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<MemberTable>) => response
        }),
        getMember: build.mutation<ApiResponse<Member>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/member/${id}`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<Member>) => response
        }),
        getMemberProfile: build.mutation<ApiResponse<MemberTable>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/member/${id}/profile`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<MemberTable>) => response
        }),
        postMembers: build.mutation<ApiResponse<Member>, MemberToCreate>({
            query: (member: MemberToCreate) => {
                return {
                    url: `/member/member`,
                    method: 'POST',
                    body: member
                };
            },
            transformResponse: (response: ApiResponse<Member>) => response
        }),
        putMembers: build.mutation<ApiResponse<Member>, { id: string; member: MemberToCreate & { id?: number } }>({
            query: ({ id, member }) => {
                return {
                    url: `/member/member/${id}`,
                    method: 'PUT',
                    body: member
                };
            },
            transformResponse: (response: ApiResponse<Member>) => response
        }),
        toggleArchiveMember: build.mutation<ApiResponse<Member>, { id: string | number; archived: boolean }>({
            query: ({ id, archived }) => {
                return {
                    url: `/member/member/${id}/archive`,
                    method: 'PUT',
                    body: { archived }
                };
            },
            transformResponse: (response: ApiResponse<Member>) => response
        }),
        resetPasswordMember: build.mutation<ApiResponse<Member>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/member/${id}/reset-password`,
                    method: 'PUT'
                };
            },
            transformResponse: (response: ApiResponse<Member>) => response
        }),
        getArchivedMembers: build.mutation<
            ApiPaginationResponse<MemberTable>,
            { limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/members/archived`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<MemberTable>) => response
        }),

        /* Export */
        getMembersExport: build.mutation<ApiResponse<string>, { search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/members/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
        getMembersEditionExport: build.mutation<ApiResponse<string>, {search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/members/edition/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
        getArchivedMembersExport: build.mutation<ApiResponse<string>, { search?: string; orderBy?: string; filters?: ActiveFilter[] }>({
            query: ({ search, orderBy, filters }) => {
                return {
                    url: `/member/members/archived/export`,
                    method: 'GET',
                    params: { search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiResponse<string>) => response
        }),
        /* Documents */
        getMemberDocuments: build.mutation<ApiResponse<CdnFile[]>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/member/${id}/documents`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<CdnFile[]>) => response
        }),

        deleteMemberDocument: build.mutation<ApiResponse<void>, { id: string; documentId: string }>({
            query: ({ id, documentId }) => {
                return {
                    url: `/member/member/${id}/document/${documentId}`,
                    method: 'DELETE'
                };
            },
            transformResponse: (response: ApiResponse<void>) => response
        }),

        postMemberDocument: build.mutation<ApiResponse<CdnFile>, { id: string; file: CdnFile }>({
            query: ({ id, file }) => {
                return {
                    url: `/member/member/${id}/document`,
                    method: 'POST',
                    body: file
                };
            },
            transformResponse: (response: ApiResponse<CdnFile>) => response
        }),

        /* Social Benefits */
        getMemberSocialBenefits: build.mutation<
            ApiPaginationResponse<SocialBenefit>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/social-benefits`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<SocialBenefit>) => response
        }),
        getMemberArchivedSocialBenefits: build.mutation<
            ApiPaginationResponse<SocialBenefit>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/social-benefits/archived`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<SocialBenefit>) => response
        }),
        addMemberSocialBenefit: build.mutation<ApiResponse<SocialBenefit>, { id: string; socialBenefit: MemberSocialBenefitToCreate }>({
            query: ({ id, socialBenefit }) => {
                return {
                    url: `/member/member/${id}/social-benefit`,
                    method: 'POST',
                    body: socialBenefit
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefit>) => response
        }),
        updateMemberSocialBenefit: build.mutation<ApiResponse<SocialBenefit>, { id: string; socialBenefit: MemberSocialBenefitToUpdate }>({
            query: ({ id, socialBenefit }) => {
                return {
                    url: `/member/member/${id}/social-benefit/${socialBenefit.id}`,
                    method: 'PUT',
                    body: socialBenefit
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefit>) => response
        }),
        archiveMemberSocialBenefit: build.mutation<ApiResponse<SocialBenefit>, { id: string; socialBenefitId: number }>({
            query: ({ id, socialBenefitId }) => {
                return {
                    url: `/member/member/${id}/social-benefit/${socialBenefitId}/archive`,
                    method: 'PUT'
                };
            },
            transformResponse: (response: ApiResponse<SocialBenefit>) => response
        }),

        /* Financial Benefits */
        getMemberFinancialBenefits: build.mutation<
            ApiPaginationResponse<FinancialBenefit>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/financial-benefits`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<FinancialBenefit>) => response
        }),
        getMemberArchivedFinancialBenefits: build.mutation<
            ApiPaginationResponse<FinancialBenefit>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/financial-benefits/archived`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<FinancialBenefit>) => response
        }),
        addMemberFinancialBenefit: build.mutation<
            ApiResponse<FinancialBenefit>,
            { id: string; financialBenefit: FinancialBenefitToCreate }
        >({
            query: ({ id, financialBenefit }) => {
                return {
                    url: `/member/member/${id}/financial-benefit`,
                    method: 'POST',
                    body: financialBenefit
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),
        updateMemberFinancialBenefit: build.mutation<
            ApiResponse<FinancialBenefit>,
            { id: string; financialBenefit: FinancialBenefitToUpdate }
        >({
            query: ({ id, financialBenefit }) => {
                return {
                    url: `/member/member/${id}/financial-benefit/${financialBenefit.id}`,
                    method: 'PUT',
                    body: financialBenefit
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),

        toggleArchiveMemberFinancialBenefit: build.mutation<ApiResponse<FinancialBenefit>, { memberId: string | number; financialBenefitId: string | number; archived: boolean }>({
            query: ({ memberId, financialBenefitId, archived }) => {
                return {
                    url: `/member/member/${memberId}/financial-benefit/${financialBenefitId}/archive`,
                    method: 'PUT',
                    body: { archived }
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),

        updateMemberFinancialBenefitSchedule: build.mutation<
            ApiResponse<FinancialBenefit>,
            { id: string; financialBenefitId: number; financialBenefitScheduleId: number; data: FinancialBenefitScheduleToUpdate }
        >({
            query: ({ id, financialBenefitId, financialBenefitScheduleId, data }) => {
                return {
                    url: `/member/member/${id}/financial-benefit/${financialBenefitId}/financial-benefit-schedule/${financialBenefitScheduleId}`,
                    method: 'PUT',
                    body: data
                };
            },
            transformResponse: (response: ApiResponse<FinancialBenefit>) => response
        }),
        /* Contributions */
        getMemberContributions: build.mutation<
            ApiPaginationResponse<Contribution>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/contributions`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<Contribution>) => response
        }),
        getMemberArchivedContributions: build.mutation<
            ApiPaginationResponse<Contribution>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/contributions/archived`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<Contribution>) => response
        }),
        addMemberContribution: build.mutation<ApiResponse<Contribution>, { id: string; contribution: ContributionToCreate }>({
            query: ({ id, contribution }) => {
                return {
                    url: `/member/member/${id}/contribution`,
                    method: 'POST',
                    body: contribution
                };
            },
            transformResponse: (response: ApiResponse<Contribution>) => response
        }),
        updateMemberContribution: build.mutation<ApiResponse<Contribution>, { id: string; contribution: ContributionToUpdate }>({
            query: ({ id, contribution }) => {
                return {
                    url: `/member/member/${id}/contribution/${contribution.id}`,
                    method: 'PUT',
                    body: contribution
                };
            },
            transformResponse: (response: ApiResponse<Contribution>) => response
        }),
        archiveMemberContribution: build.mutation<ApiResponse<Contribution>, { id: string; contributionId: number; archived?: boolean }>({
            query: ({ id, contributionId, archived = true }) => {
                return {
                    url: `/member/member/${id}/contribution/${contributionId}/archive`,
                    method: 'PUT',
                    body: { archived }
                };
            },
            transformResponse: (response: ApiResponse<Contribution>) => response
        }),
        checkMemberContribution: build.mutation<ApiResponse<Contribution>, { id: string; contribution: ContributionToCheck }>({
            query: ({ id, contribution }) => {
                return {
                    url: `/member/member/${id}/contribution/${contribution.id}/check`,
                    method: 'PUT',
                    body: contribution
                };
            },
            transformResponse: (response: ApiResponse<Contribution>) => response
        }),
        /* Ticketing */
        getMemberTicketing: build.mutation<
            ApiPaginationResponse<TicketingTable>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/member/${id}/ticketing`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<TicketingTable>) => response
        }),
        getMemberArchivedTicketing: build.mutation<
            ApiPaginationResponse<TicketingTable>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string; filters?: ActiveFilter[] }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy, filters }) => {
                return {
                    url: `/member/member/${id}/ticketing/archived`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '', filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<TicketingTable>) => response
        }),
        archiveMemberTicketing: build.mutation<ApiResponse<Ticketing>, { id: string; ticketingId: number }>({
            query: ({ id, ticketingId }) => {
                return {
                    url: `/member/member/${id}/ticketing/${ticketingId}/archive`,
                    method: 'PUT'
                };
            },
            transformResponse: (response: ApiResponse<Ticketing>) => response
        }),
        /* Bookings */
        getMemberActiveBookings: build.mutation<
            ApiPaginationResponse<BookingTable>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/bookings/active`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getMemberPastBookings: build.mutation<
            ApiPaginationResponse<BookingTable>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/bookings/past`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getMemberFutureBookings: build.mutation<
            ApiPaginationResponse<BookingTable>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/bookings/future`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getMemberAllBookings: build.mutation<
            ApiPaginationResponse<BookingTable>,
            { id: string; limit?: number; page?: number; search?: string; orderBy?: string }
        >({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return {
                    url: `/member/member/${id}/bookings/all`,
                    method: 'GET',
                    params: { limit, page, search: search ?? '', orderBy: orderBy ?? '' }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getMemberBookingRange: build.mutation<ApiResponse<BookingRange[]>, { id: string; start: string; end: string }>({
            query: ({ id, start, end }) => {
                return {
                    url: `/member/member/${id}/bookings/range`,
                    method: 'GET',
                    params: {
                        start: start,
                        end: end
                    }
                };
            },
            transformResponse: (response: ApiResponse<BookingRange[]>) => response
        })
    })
});

export const {
    useGetMembersMutation,
    useGetMemberMutation,
    useGetArchivedMembersMutation,
    usePostMembersMutation,
    useGetMemberProfileMutation,
    useToggleArchiveMemberMutation,
    usePutMembersMutation,
    useGetMemberDocumentsMutation,
    useDeleteMemberDocumentMutation,
    usePostMemberDocumentMutation,
    useGetMemberSocialBenefitsMutation,
    useGetMemberArchivedSocialBenefitsMutation,
    useAddMemberSocialBenefitMutation,
    useUpdateMemberSocialBenefitMutation,
    useArchiveMemberSocialBenefitMutation,
    useGetMemberFinancialBenefitsMutation,
    useGetMemberArchivedFinancialBenefitsMutation,
    useAddMemberFinancialBenefitMutation,
    useUpdateMemberFinancialBenefitMutation,
    useToggleArchiveMemberFinancialBenefitMutation,
    useUpdateMemberFinancialBenefitScheduleMutation,
    useGetMemberContributionsMutation,
    useResetPasswordMemberMutation,
    useGetMemberArchivedContributionsMutation,
    useAddMemberContributionMutation,
    useUpdateMemberContributionMutation,
    useArchiveMemberContributionMutation,
    useCheckMemberContributionMutation,
    useGetMemberTicketingMutation,
    useGetMemberArchivedTicketingMutation,
    useArchiveMemberTicketingMutation,
    useGetMemberAllBookingsMutation,
    useGetArchivedMembersExportMutation,
    useGetMembersExportMutation,
    useGetMemberActiveBookingsMutation,
    useGetMemberPastBookingsMutation,
    useGetMemberFutureBookingsMutation,
    useGetMemberBookingRangeMutation,
    useGetMembersEditionMutation,
    useGetMembersEditionExportMutation
} = memberApi;
